/**
 * A message's attachments as `GET /api/threads/:id` lists them (slice 2f
 * spec §10): its own photos and files, and whether its body is only the
 * Worker's placeholder for them.
 */
import { API_URL } from "../../app/api";

/** A file Crew keeps, from its own copy (2f §10.4): never another host. */
export const fileUrl = (id: string, size?: "thumb" | "full") =>
  `${API_URL}/api/files/${id}${size ? `?size=${size}` : ""}`;

/** An attachment as `GET /api/threads/:id` lists it; `doc_id` once it's kept in Docs (slice 5b). */
export type Attachment = {
  id: string;
  name: string;
  mime: string;
  size: number;
  doc_id?: string | null;
};

function listOf(value: unknown): Attachment[] {
  if (!Array.isArray(value)) return [];
  return value.filter((a): a is Attachment => typeof (a as Attachment | null)?.id === "string");
}

/** A message's own photos and files. */
export function attachmentsOf(data: Record<string, unknown>) {
  const passedOn = typeof data.photos_from === "string";
  return { photos: passedOn ? [] : listOf(data.photos), files: listOf(data.files) };
}

/**
 * The body the Worker gives a text with no words ("📷 Photo", "📷 2 photos",
 * "📎 Lease.pdf", "📎 2 files"): not shown once the attachments are.
 */
export function isPlaceholder(body: string, a: ReturnType<typeof attachmentsOf>): boolean {
  const n = a.photos.length;
  const all = n + a.files.length;
  // Photos and files sent together in the app (slice 5b): "📎 3 files".
  if (n > 0 && a.files.length > 0) return body === `📎 ${all} files`;
  if (n > 0) return body === (n === 1 ? "📷 Photo" : `📷 ${n} photos`);
  if (a.files.length === 1) return body === `📎 ${a.files[0]!.name}`;
  return a.files.length > 1 && body === `📎 ${a.files.length} files`;
}

/** A forwarded original's file (slice 2h §6.2), "Forwarded - <subject>.md": its subject, else null. */
export function forwardedSubject(name: string): string | null {
  const m = /^Forwarded - (.*)\.md$/.exec(name);
  return m ? m[1]! : null;
}
